/*
 * Copyright '2022-24' Dell Inc. or its subsidiaries. All Rights Reserved.
 */

import { IDependencyContainer } from "sirius-platform-support-library/dependency-injection/generic/dependency-container.interface";
import { TenantContext } from "sirius-platform-support-library/tenants/tenant-context";
import { ITenantCustomBootstrapCode } from "sirius-shell-support-library/tenants/custom-code/tenant-custom-bootstrap-code.interface";
import {
  TdmManagedSessionCookieUserInfoService,
  TdmManagedSessionCookieUserInfoServiceTypeName,
} from "./core/authentication/tdm-managed-session-cookie.user-info.service";
import { IManagedSessionCookieUserInfoServiceTypeName } from "sirius-platform-support-library/shared/authentication/handlers/dell-identity/managed-session-cookie/services/managed-session-cookie.user-info.service.interface";
import { IRequiredClaimsMappingDelegatedProviderTypeName } from "sirius-platform-support-library/shared/authentication/claims/required-claims-mapping.delegated-provider.interface";
import {
  IFeatureTogglesProvider,
  IFeatureTogglesProviderTypeName,
} from "sirius-platform-support-library/shared/feature-toggles/providers/feature-toggles-provider.interface";
import {
  TdpFeatureTogglesProvider,
  TdpFeatureTogglesProviderTypeName,
} from "./core/feature-toggles/tdp-feature-toggles.provider";
import { FeatureToggleMapper } from "./core/feature-toggles/mappers/feature-toggle.mapper";
import { environment } from "../environments/environments";
import {
  IEventBus,
  IEventBusTypeName,
} from "sirius-platform-support-library/shared/event-bus/event-bus.interface";
import {
  IAuthenticationService,
  IAuthenticationServiceTypeName,
} from "sirius-platform-support-library/shared/authentication/authentication-service.interface";
import {
  FeedbackHandler,
  FeedbackHandlerTypeName,
} from "./core/feedback/handler/feedback-handler";
import {
  IDialogsService,
  IDialogsServiceTypeName,
} from "sirius-platform-support-library/shared/dialogs/dialogs-service.interface";
import { TranslationService } from "sirius-platform-support-library/shared/localization/translations/translation.service";
import { SupportedTranslationsLoaders } from "sirius-platform-support-library/shared/localization/translations/loaders/translations-loaders.constants";
import { SessionStorageService } from "./core/services/session-storage/session-storage.service";
import { FeedbackService } from "./core/feedback/services/feedback.service";
import {
  IPermissionsProvider,
  IPermissionsProviderTypeName,
} from "sirius-platform-support-library/shared/authorization/providers/permissions-provider.interface";
import {
  BannerAlertsProvider,
  BannerAlertsProviderTypeName,
} from "./core/banner-alerts/provider/banner-alerts.provider";
import {
  IBannerAlertsProvider,
  IBannerAlertsProviderTypeName,
} from "sirius-platform-support-library/shared/banner-alerts/providers/banner-alerts-provider.interface";
import { ServicePageNotificationService } from "./core/banner-alerts/services/service-page-notification.service";
import { BannerAlertsMapper } from "./core/banner-alerts/mappers/banner-alerts.mapper";
import {
  IBannerAlertsPriorityAssigner,
  IBannerAlertsPriorityAssignerTypeName,
} from "sirius-platform-support-library/shared/banner-alerts/banner-alerts-priority-assigner.interface";
import {
  TdpBannerAlertsPriorityAssigner,
  TdpBannerAlertsPriorityAssignerTypeName,
} from "./core/banner-alerts/priority-assigner/tdp-banner-alerts-priority-assigner";
import {
  ILocalizationService,
  ILocalizationServiceTypeName,
} from "sirius-platform-support-library/shared/localization/localization-service.interface";
import { LocalizedDateUtility } from "./core/banner-alerts/services/localized-date.utility";
import {
  NotificationsProvider,
  NotificationsProviderTypeName,
} from "./core/notifications/providers/notifications.provider";
import {
  INotificationsProvider,
  INotificationsProviderTypeName,
} from "sirius-platform-support-library/shared/notifications/providers/notifications-provider.interface";
import { NotificationsService } from "./core/notifications/services/notifications.service";
import {
  ContactUsActionHandler,
  ContactUsActionHandlerTypeName,
} from "./core/event-bus/contact-us-action.handler";
import { LanguageService } from "./core/i18n/language.service";
import { TogglesProvider } from "./core/toggles/toggles-provider";
import { ToastsService } from "./core/toasts/toasts.service";
import { toastCss } from "./core/toasts/toast-css";
import { CommonElements } from "./core/elements/common-elements";
import { RoutesBootstrap } from "./core/routes/routes-bootstrap";
import { CatalogToApplicationDefinitionAdapter } from "./core/services/catalog/catalog-to-application-definition-adapter";
import { CatalogsService } from "./core/services/catalog/catalogs.service";
import { IManagedSessionCookieSessionServiceTypeName } from "sirius-platform-support-library/shared/authentication/handlers/dell-identity/managed-session-cookie/services/managed-session-cookie.session.service.interface";
import { MfeFrameworkLoader } from "./core/framework/mfe-framework-loader";
import {
  TdpAnalyticsService,
  TdpAnalyticsServiceTypeName,
} from "./core/analytics/tdpAnalyticsService";
import {
  IAnalyticsService,
  IAnalyticsServiceTypeName,
} from "sirius-platform-support-library/shared/analytics/analytics-service.interface";
import { PendoUserDataService } from "./core/analytics/pendo-user-data.service";
import { TdmApolloClient } from "./core/client/tdm-apollo-client";
import {
  ActionHandlerInterceptor,
  ActionHandlerInterceptorTypeName,
} from "./core/action/interceptors/action-handler-interceptor";
import {
  IActionHandlerInterceptor,
  IActionHandlerInterceptorTypeName,
} from "sirius-platform-support-library/shared/actions/interceptors/action-handler-interceptor.interface";
import { DynamicImportUtility } from "sirius-platform-support-library/utilities/dynamic-import.utility";

// Importing the Clarity Design System CSS
import "@uxd/clarity-theme/dist/uxd-color-palette.scss";
import "./styles/styles.css";
import { MfeFramework } from "si-mfes-library/framework/mfe-framework";
import {
  ILocaleProviderPriorityAssigner,
  ILocaleProviderPriorityAssignerTypeName,
} from "sirius-platform-support-library/shared/localization/providers/locale-provider-priority-assigner.interface";
import { DEFAULT_LOCALE_PROVIDER_PRIORITIES } from "./core/localization/locale-provider-priority";
import { IBrowserNavigationEventsReceiverTypeName } from "sirius-platform-support-library/shared/browser-events/browser-navigation-events-receiver.interface";
import {
  UserActivityNavigationGateHandler,
  UserActivityNavigationGateHandlerTypeName,
} from "./core/navigation/user-activity-navigation.handler";
import {
  IBrowserNavigationGateHandler,
  IBrowserNavigationGateHandlerTypeName,
} from "sirius-platform-support-library/shared/browser-events/browser-navigation-gate-handler.interface";
import {
  UserClaimsPermissionProvider,
  UserClaimsPermissionProviderTypeName,
} from "./core/authorization/user-claims-permission.provider";
import {
  UserNavigationInterceptor,
  UserNavigationInterceptorTypeName,
} from "./core/action/interceptors/user-navigation.interceptor";
import {
  IEnvironmentCodeProvider,
  IEnvironmentCodeProviderTypeName,
} from "sirius-platform-support-library/shared/environment/environment-code-provider.interface";
import {
  PermanentRedirectNaviationGateHandlerTypeName,
  PermanentRedirectNavigationGateHandler,
} from "./core/navigation/permanent-redirect.handler";
import { redirectRules } from "../../permanent-redirects.json";

export class TenantCustomBootstrapCode implements ITenantCustomBootstrapCode {
  private static readonly TDM_LOCALE_KEY = "Dell_TechDirect_user_language";
  private static readonly SIRIUS_LOCALE_KEY = "sirius.localization.locale-code";
  private static readonly FOOTER_SELECTOR =
    ".site-wrapper .site-content .footer-marker";

  private translationService?: TranslationService;
  private contactUsActionHandler?: ContactUsActionHandler;
  private feedBackHandler?: FeedbackHandler;

  // private environmentCodeProvider?: IEnvironmentCodeProvider;
  private localizationService?: ILocalizationService;
  private localeProviderPriorityAssigner?: ILocaleProviderPriorityAssigner;
  private languageService?: LanguageService;
  private togglesProvider?: TogglesProvider;
  private toastsService?: ToastsService;
  private commonElements?: CommonElements;
  private routesBootstrap?: RoutesBootstrap;
  private readonly mfeFrameworkLoader: MfeFrameworkLoader;
  private tdpAnalyticsService?: TdpAnalyticsService;
  private authenticationService?: IAuthenticationService;
  private bannerAlertsProvider?: IBannerAlertsProvider;
  private readonly mfeFramework?: MfeFramework;

  public constructor() {
    this.togglesProvider = new TogglesProvider(window);
    this.mfeFramework = MfeFramework.getInstance();
    this.mfeFrameworkLoader = new MfeFrameworkLoader(window, this.mfeFramework);
    this.mfeFramework.Toggles.registerToggleProviderDelegate(
      this.togglesProvider
    );

    Promise.allSettled([
      DynamicImportUtility.import(
        "/libs/@clr/icons/clr-icons.min.css",
        "link",
        document.head,
        { rel: "stylesheet" }
      ),
      DynamicImportUtility.import(
        "/libs/@clr/ui/clr-ui.min.css",
        "link",
        document.head,
        { rel: "stylesheet" }
      ),
      DynamicImportUtility.import(
        "/libs/@uxd/clarity-theme/dist/uxd-clarity-theme.css",
        "link",
        document.head,
        { rel: "stylesheet" }
      ),
      DynamicImportUtility.import(
        "/libs/@clr/icons/clr-icons.min.js",
        "script",
        document.head
      ),
    ]).catch((error) => {
      // Swallowing error as desired
    });
  }

  public async bootstrap(
    tenantContext: TenantContext,
    dependencyContainer: IDependencyContainer
  ): Promise<void> {
    await this.mfeFrameworkLoader.bootstrap();

    this.translationService = new TranslationService({
      publicPath: {
        provide: () =>
          // @ts-ignore
          __webpack_public_path__,
      },
      type: SupportedTranslationsLoaders.REMOTE_ASSETS,
      options: {
        baseUrl:
          environment.cdnBaseURL +
          "tdp-assets-library" +
          environment.assetsLibraryVersion,
        resourceNameMapper: (localeCode) =>
          "/resources/tdp-common/" + localeCode.replace(/_/g, "-") + ".json",
      },
    });

    dependencyContainer.register((container) => {
      container
        .addUsingInstanceAsSingleton<TdmManagedSessionCookieUserInfoService>(
          TdmManagedSessionCookieUserInfoServiceTypeName,
          new TdmManagedSessionCookieUserInfoService(window)
        )
        .addInterfacesOnSingleton(
          TdmManagedSessionCookieUserInfoServiceTypeName,
          IManagedSessionCookieUserInfoServiceTypeName
        )
        .addInterfacesOnSingleton(
          TdmManagedSessionCookieUserInfoServiceTypeName,
          IManagedSessionCookieSessionServiceTypeName
        )
        .addInterfacesOnSingleton(
          TdmManagedSessionCookieUserInfoServiceTypeName,
          IRequiredClaimsMappingDelegatedProviderTypeName
        )
        .addUsingFactoryAsSingleton<
          IFeatureTogglesProvider,
          TdpFeatureTogglesProvider
        >(
          IFeatureTogglesProviderTypeName,
          TdpFeatureTogglesProviderTypeName,
          (di) => {
            const mapper = new FeatureToggleMapper(
              TdpFeatureTogglesProvider.NAMESPACE
            );
            return new TdpFeatureTogglesProvider(window, mapper, {
              endpoint: environment.releaseTogglesEndpoint,
            });
          }
        )
        .addUsingFactoryAsSingleton<
          IPermissionsProvider,
          UserClaimsPermissionProvider
        >(
          IPermissionsProviderTypeName,
          UserClaimsPermissionProviderTypeName,
          (di) => {
            const authenticationService = di.resolve<IAuthenticationService>(
              IAuthenticationServiceTypeName
            );
            return new UserClaimsPermissionProvider(authenticationService);
          }
        )
        .addUsingFactoryAsSingleton<
          IBrowserNavigationGateHandler,
          UserActivityNavigationGateHandler
        >(
          IBrowserNavigationGateHandlerTypeName,
          UserActivityNavigationGateHandlerTypeName,
          (di) => {
            const authenticationService = di.resolve<IAuthenticationService>(
              IAuthenticationServiceTypeName
            );
            return new UserActivityNavigationGateHandler(
              window,
              authenticationService
            );
          }
        )
        .addInterfacesOnSingleton(
          UserActivityNavigationGateHandlerTypeName,
          IBrowserNavigationEventsReceiverTypeName
        )
        .addUsingFactoryAsSingleton<
          IBrowserNavigationGateHandler,
          PermanentRedirectNavigationGateHandler
        >(
          IBrowserNavigationGateHandlerTypeName,
          PermanentRedirectNaviationGateHandlerTypeName,
          (di) => {
            return new PermanentRedirectNavigationGateHandler(
              window,
              this.togglesProvider,
              redirectRules
            );
          }
        )
        .addUsingFactoryAsSingleton<
          INotificationsProvider,
          NotificationsProvider
        >(
          INotificationsProviderTypeName,
          NotificationsProviderTypeName,
          (di) => {
            const notificationsService = new NotificationsService(window);
            const authenticationService = di.resolve<IAuthenticationService>(
              IAuthenticationServiceTypeName
            );
            return new NotificationsProvider(
              notificationsService,
              authenticationService
            );
          }
        )
        .addUsingInstanceAsSingleton<
          IBannerAlertsPriorityAssigner,
          TdpBannerAlertsPriorityAssigner
        >(
          IBannerAlertsPriorityAssignerTypeName,
          TdpBannerAlertsPriorityAssignerTypeName,
          new TdpBannerAlertsPriorityAssigner()
        )
        .addUsingFactoryAsSingleton<
          IBannerAlertsProvider,
          BannerAlertsProvider
        >(IBannerAlertsProviderTypeName, BannerAlertsProviderTypeName, (di) => {
          const localizationService = dependencyContainer
            .getServiceCollection()
            .resolve<ILocalizationService>(ILocalizationServiceTypeName);
          const mapper = new BannerAlertsMapper(
            BannerAlertsProvider.NAMESPACE,
            new LocalizedDateUtility(),
            this.translationService
          );
          const servicePageNotificationService =
            new ServicePageNotificationService(window);
          const authenticationService = di.resolve<IAuthenticationService>(
            IAuthenticationServiceTypeName
          );

          return new BannerAlertsProvider(
            mapper,
            servicePageNotificationService,
            authenticationService,
            localizationService,
            this.togglesProvider
          );
        })
        .addUsingFactoryAsSingleton<ContactUsActionHandler>(
          ContactUsActionHandlerTypeName,
          (di) => {
            const eventBus = di.resolve<IEventBus>(IEventBusTypeName);
            const authenticationService = di.resolve<IAuthenticationService>(
              IAuthenticationServiceTypeName
            );

            return new ContactUsActionHandler(
              eventBus,
              authenticationService,
              window
            );
          }
        )
        .addUsingFactoryAsSingleton<
          IActionHandlerInterceptor,
          ActionHandlerInterceptor
        >(
          IActionHandlerInterceptorTypeName,
          ActionHandlerInterceptorTypeName,
          (di) => {
            const authenticationService = di.resolve<IAuthenticationService>(
              IAuthenticationServiceTypeName
            );
            return new ActionHandlerInterceptor(authenticationService);
          }
        )
        .addUsingFactoryAsSingleton<
          IActionHandlerInterceptor,
          UserNavigationInterceptor
        >(
          IActionHandlerInterceptorTypeName,
          UserNavigationInterceptorTypeName,
          (di) => {
            return new UserNavigationInterceptor(this.languageService);
          }
        )
        .addUsingFactoryAsSingleton<FeedbackHandler>(
          FeedbackHandlerTypeName,
          (di) => {
            const eventBus = di.resolve<IEventBus>(IEventBusTypeName);
            const dialogsService = di.resolve<IDialogsService>(
              IDialogsServiceTypeName
            );
            const sessionStorageService = new SessionStorageService(window);
            const feedbackService = new FeedbackService(window);

            return new FeedbackHandler(
              eventBus,
              dialogsService,
              this.translationService,
              sessionStorageService,
              feedbackService
            );
          }
        )
        .addUsingFactoryAsSingleton<TdpAnalyticsService>(
          TdpAnalyticsServiceTypeName,
          (di) => {
            const analyticsService = di.resolve<IAnalyticsService>(
              IAnalyticsServiceTypeName
            );
            const localizationService = di.resolve<ILocalizationService>(
              ILocalizationServiceTypeName
            );
            const authenticationService = di.resolve<IAuthenticationService>(
              IAuthenticationServiceTypeName
            );
            const tdmApolloClient = new TdmApolloClient();
            const pendoUserDataService = new PendoUserDataService(
              tdmApolloClient
            );
            const environmentCodeProvider =
              di.resolve<IEnvironmentCodeProvider>(
                IEnvironmentCodeProviderTypeName
              );

            return new TdpAnalyticsService(
              window,
              analyticsService,
              localizationService,
              pendoUserDataService,
              authenticationService,
              environmentCodeProvider
            );
          }
        );
    });

    this.localizationService = dependencyContainer
      .getServiceCollection()
      .resolve<ILocalizationService>(ILocalizationServiceTypeName);
    this.localeProviderPriorityAssigner = dependencyContainer
      .getServiceCollection()
      .resolve<ILocaleProviderPriorityAssigner>(
        ILocaleProviderPriorityAssignerTypeName
      );
    this.contactUsActionHandler = dependencyContainer
      .getServiceCollection()
      .resolve<ContactUsActionHandler>(ContactUsActionHandlerTypeName);
    this.feedBackHandler = dependencyContainer
      .getServiceCollection()
      .resolve<FeedbackHandler>(FeedbackHandlerTypeName);
    this.tdpAnalyticsService = dependencyContainer
      .getServiceCollection()
      .resolve<TdpAnalyticsService>(TdpAnalyticsServiceTypeName);
    this.authenticationService = dependencyContainer
      .getServiceCollection()
      .resolve<IAuthenticationService>(IAuthenticationServiceTypeName);
    this.bannerAlertsProvider = dependencyContainer
      .getServiceCollection()
      .resolve<IBannerAlertsProvider>(IBannerAlertsProviderTypeName);
    this.localeProviderPriorityAssigner.configure(
      DEFAULT_LOCALE_PROVIDER_PRIORITIES
    );
    this.patchLocalStorageLocale();

    await this.authenticationService.silentSignIn(false);
    this.authenticationService.onSignedIn(
      this,
      "BannerAlertProvider",
      async () => {
        await this.bannerAlertsProvider.refresh();
      }
    );
  }

  public async initialized(): Promise<void> {
    this.languageService = new LanguageService(
      this.mfeFramework,
      this.localizationService
    );
    await this.languageService.bootstrap();

    const catalogsService = new CatalogsService(window);
    const catalogToApplicationDefinitionAdapter =
      new CatalogToApplicationDefinitionAdapter(catalogsService);

    this.commonElements = new CommonElements(
      window,
      this.languageService,
      this.mfeFramework,
      this.localizationService
    );
    this.routesBootstrap = new RoutesBootstrap(
      window,
      this.mfeFrameworkLoader,
      catalogToApplicationDefinitionAdapter,
      this.authenticationService,
      this.localizationService
    );
    await this.routesBootstrap.bootstrap(
      this.commonElements.bootstrap.bind(this.commonElements)
    );
  }

  public async ready(): Promise<void> {
    DynamicImportUtility.import(
      `${environment.cdnBaseURL}${environment.chatBotResourceRoute}`,
      "script",
      document.head
    ).catch((e) =>
      console.error(
        "Failed to load chat widget due to an encountered error: ",
        e
      )
    );
    await this.injectToastContainer();
    this.toastsService = new ToastsService(window, ".toast-container");

    this.contactUsActionHandler?.registerActionHandler();
    this.feedBackHandler?.registerFeedbackHandler();
    this.observeShadowHost();
    await this.tdpAnalyticsService?.init();
  }

  public async injectToastContainer(): Promise<void> {
    const mainContainer = document.querySelector(".main-section");
    if (!mainContainer) {
      return;
    }

    const toastElementContainer = document.createElement("div");
    toastElementContainer.classList.add("toast-container");

    const styleElement = document.createElement("style");
    styleElement.textContent = toastCss;

    toastElementContainer.appendChild(styleElement);
    mainContainer.appendChild(toastElementContainer);
  }

  private patchLocalStorageLocale(): void {
    const localeCode = window.localStorage.getItem(
      TenantCustomBootstrapCode.TDM_LOCALE_KEY
    );
    const supportedLocale =
      this.mfeFramework.SupportedLocales.isLocaleSupported(localeCode);
    if ((!localeCode || !supportedLocale) && this.localizationService) {
      window.localStorage.setItem(
        TenantCustomBootstrapCode.TDM_LOCALE_KEY,
        this.localizationService.getDefaultLocaleCode()
      );
    }

    const siriusLocaleCode = window.localStorage.getItem(
      TenantCustomBootstrapCode.SIRIUS_LOCALE_KEY
    );
    const siriusSupportedLocale =
      this.mfeFramework.SupportedLocales.isLocaleSupported(siriusLocaleCode);
    if (
      (!siriusLocaleCode || !siriusSupportedLocale) &&
      this.localizationService
    ) {
      window.localStorage.setItem(
        TenantCustomBootstrapCode.SIRIUS_LOCALE_KEY,
        this.localizationService.getDefaultLocaleCode()
      );
    }
  }

  private observeShadowHost(): void {
    const footerElement = document.body.querySelector<Element>(
      TenantCustomBootstrapCode.FOOTER_SELECTOR
    );
    if (!footerElement) {
      return;
    }
    const observer = new MutationObserver((mutationsList) => {
      const shadowHost = document.querySelector("app-sirius-footer-spa-mfe");
      for (const mutation of mutationsList) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          if (shadowHost?.shadowRoot) {
            const elementInsideShadow =
              shadowHost.shadowRoot.querySelector(".uxd-footer");
            if (elementInsideShadow) {
              // @ts-ignore
              elementInsideShadow.style.padding = "40px";
            }
          } else {
            this.observeShadowRoot(observer, shadowHost);
          }
        }
      }
    });

    const observerConfig = { childList: true, subtree: true };
    observer.observe(footerElement, observerConfig);
  }

  private observeShadowRoot(
    mainObserver: MutationObserver,
    shadowHost: Element
  ): void {
    const shadowRootObserver = new MutationObserver(() => {
      const shadowRoot = shadowHost.shadowRoot;
      if (shadowRoot) {
        const elementInsideShadow = shadowRoot.querySelector(".uxd-footer");
        if (elementInsideShadow) {
          // @ts-ignore
          elementInsideShadow.style.padding = "40px";
          shadowRootObserver.disconnect();
          mainObserver.disconnect();
        }
      }
    });

    const observerConfig = { childList: true, subtree: true };
    shadowRootObserver.observe(shadowHost, observerConfig);
  }
}
